import strings from '../Globalization/index';
import Connection from '../services/Connection';
import { onError } from '../components/crud-helper';
import util from "./index";

const catAdminUtils = {

	/**
	  * Adds or updates data by sending a POST request to the specified API URL.
	  *
	  * @async
	  * @function addData
	  * @param {Object} params - The parameters object.
	  * @param {Object} params.values - The form values or data to be saved.
	  * @param {Object} params.activeRecord - The active record object, containing the `id` if updating.
	  * @param {Function} params.navigate - Function to navigate to a different route after successful save.
	  * @param {string} params.apiUrl - The API endpoint URL for saving data.
	  * @param {string} params.navigateUrl - The URL or route to navigate to after successful save.
	  * @returns {Promise<void>} - Returns nothing. Handles success and error internally.
	  */
	addData: async ({ values, activeRecord, navigate, apiUrl, navigateUrl, searchParams }) => {
		const response = await fetch(apiUrl, {
			method: 'POST',
			body: JSON.stringify({ action: 'save', ...values, id: activeRecord.id || 0 }),
			headers: { 'Content-Type': 'application/json' }
		})
		const { success, info, err, ...rest } = await response.json();
		if (!success) {
			onError({ err, info, ...rest })
			return;
		}
		util.showError(
			strings.SaveSuccessText,
			strings.Success,
			'success'
		);
		const returnTo = decodeURIComponent(searchParams.get('returnTo') || '');
		if (returnTo) {
			navigate(returnTo);
			return;
		}
		navigate(navigateUrl);
	},

	/**
	  * Handles form submission with pre-save validation on record count.
	  *
	  * @async
	  * @function handleOnSubmit
	  * @param {Object} params - The parameters object.
	  * @param {Object} params.values - The form values to be submitted.
	  * @param {Object} params.activeRecord - The currently active record (contains the `id`).
	  * @param {number} params.maxCount - The maximum allowed record count before showing a warning.
	  * @param {string} params.message - The warning message to show if record count exceeds the limit.
	  * @param {string} params.apiUrl - The API endpoint URL to use for saving or listing data.
	  * @param {Function} params.navigate - Navigation function to redirect after successful save.
	  * @param {string} params.navigateUrl - The URL or route to navigate to after successful save.
	  * @returns {Promise<void>} - Returns nothing. Handles flow control internally.
	  */

	handleOnSubmit: async ({ values, activeRecord, maxCount, message, apiUrl, navigate, navigateUrl, searchParams }) => {
		const isAllowToShowWarning = ([0, '', null, undefined].includes(activeRecord.id) || apiUrl === Connection.HuluScopeEndpoint);
		const url = apiUrl === Connection.HuluScopeEndpoint ? Connection.ItemSync : apiUrl;
		try {
			if (isAllowToShowWarning) {
				const response = await fetch(url, {
					method: 'POST',
					body: JSON.stringify({ action: 'list' }),
					headers: { 'Content-Type': 'application/json' }
				})
				const { recordCount } = await response.json();
				if (recordCount >= maxCount) {
					util.showError(
						message,
						strings.Warning,
						'warning',
						() => catAdminUtils.addData({ values, activeRecord, apiUrl, navigate, navigateUrl, searchParams })
					);
					return;
				} else {
					await catAdminUtils.addData({ values, activeRecord, apiUrl, navigate, navigateUrl, searchParams })
				}
			}
			else {
				await catAdminUtils.addData({ values, activeRecord, apiUrl, navigate, navigateUrl, searchParams });
			}
		} catch (err) {
			console.error(err);
		}
	},

	/**
	  * Fetches the total record count from the specified API endpoint.
	  *
	  * @async
	  * @function getCount
	  * @param {string} apiUrl - The API endpoint URL to fetch the record count from.
	  * @returns {Promise<number>} - Returns a promise that resolves to the number of records (`recordCount`).
	  */

	getCount: async (apiUrl) => {
		const response = await fetch(apiUrl, {
			method: 'POST',
			body: JSON.stringify({ action: 'list' }),
			headers: { 'Content-Type': 'application/json' }
		})
		const { recordCount } = await response.json();

		return recordCount;
	}
}

export default catAdminUtils;